import React, { Fragment } from 'react';
import { useLocation } from 'react-router';

import { CustomFonts } from 'app/components/custom-fonts';
import { ErrorModal } from 'app/components/error-modal';
import { ErrorNavigator } from 'app/components/error-navigator';
import { OpenAppParamsGuard } from 'app/components/open-app-params-guard';
import { LoadingScreen } from 'app/components/ui/loading-screen';
import { ROUTES } from 'app/shared/consts/paths';
import { useAnalytics } from 'app/shared/hooks/use-analytics-hook';
import { useBuilding } from 'app/shared/contexts/building';
import { useHideNativeHeader } from 'app/shared/hooks/use-hide-native-header';
import { useTheme } from 'app/shared/contexts/theme';

interface AppContentWrapperProps {
  children: React.ReactNode;
}

/**
 * Shell that wraps every route. Mounts the global ErrorModal (recoverable
 * popup) and ErrorNavigator (redirects to /error on terminal errors —
 * `genericErrors` channel of ErrorsContext).
 *
 * Boot UX: render a spinner while theme/building haven't resolved yet,
 * **except** on `/error`. The error route must always render so terminal
 * errors during boot (no apiUrl, foundational query failed) actually surface
 * instead of being hidden behind the spinner.
 */
export const AppContentWrapper = ({ children }: AppContentWrapperProps): JSX.Element => {
  const { theme } = useTheme();
  const { building } = useBuilding();
  const { pathname } = useLocation();

  // Sentry user identity + @hqo/web-tracking init. The effects inside are
  // keyed on user/building data, so they no-op until that data lands.
  // Previously hosted in <AppRoot>, which was deleted with the bootstrap
  // demo routes — needs to live here now that every entry point lands
  // straight on a service URL.
  useAnalytics();

  // Drop the host's native webview header for good once the SDK is ready —
  // the miniapp draws its own header/close controls (SMB-893).
  useHideNativeHeader();

  const isErrorRoute = pathname === ROUTES.ERROR;
  const isReady = isErrorRoute || (theme && building);

  return (
    <Fragment>
      <CustomFonts />
      <OpenAppParamsGuard />
      <ErrorNavigator />
      {isReady ? (
        <Fragment>
          {children}
          <ErrorModal />
        </Fragment>
      ) : (
        <LoadingScreen data-testid="loading-container" />
      )}
    </Fragment>
  );
};
